import { useState, type CSSProperties, type MouseEvent } from 'react'
import { Icon, type IconName } from '../../ui/Icon'
import type { RelayJournalEvent, RelayStatsSeries } from './api'
import { clock, ms } from './format'

/*
 * Series of a route (GET /routes/{id}/stats): latency per hop (p50) and
 * end-to-end (p50, last hop), plus frames per step with gaps — three small
 * charts on one time axis, never two y-scales in one chart
 * (design/network-console/proto/src/routes/TraceChart.tsx). A missing point
 * (no report in that step) breaks the line instead of being drawn as zero.
 * Markers: resumes and gaps of the route's journal.
 */

/**
 * Hop series in the system's categorical colours chart-1…4, in order (03 «only chart-1…4», AUDIT
 * ST-06): never a state colour, never the accent. Line styles still differ by dash, so a series
 * does not depend on colour. End-to-end is the text colour; frames are context.
 */
const HOP_DASH = ['', '6 3', '2 3', '10 3 2 3']
const hopColour = (i: number) => `var(--chart-${(i % 4) + 1})`
const hopStyle = (i: number) => ({ style: { stroke: hopColour(i) } as CSSProperties, dash: HOP_DASH[i % 4]! })

type MarkerKind = 'resume' | 'gap'
/** Journal markers are events with a state: labelled dashed rules in the state's colour, icon + word in the legend. */
const MARK: Record<MarkerKind, { stroke: string; fill: string; text: string; icon: IconName; word: string; dash: string }> = {
  resume: { stroke: 'stroke-start', fill: 'fill-start', text: 'text-start', icon: 'refresh', word: 'Hop resumed', dash: '4 3' },
  gap: { stroke: 'stroke-fail', fill: 'fill-fail', text: 'text-fail', icon: 'state-fail', word: 'Gap', dash: '2 3' },
}

const STEP_MS: Record<RelayStatsSeries['step'], number> = { '5s': 5000, '1m': 60_000, '1h': 3_600_000 }

export interface Chart {
  t: number[]
  hops: { name: string; values: (number | null)[] }[]
  e2e: (number | null)[]
  frames: (number | null)[]
  gaps: (number | null)[]
  suspect: boolean[]
  markers: { t: number; kind: MarkerKind; label: string }[]
}

/** Server series → one time axis; steps with no point in any series stay null. */
export function toChart(s: RelayStatsSeries, journal: RelayJournalEvent[], nameOf: (id: string) => string = (id) => id): Chart {
  const step = STEP_MS[s.step]
  const all = s.series.flatMap((x) => x.points.map((p) => Date.parse(p.t)))
  const t: number[] = []
  if (all.length) {
    const t0 = Math.min(...all)
    const t1 = Math.max(...all)
    for (let x = t0; x <= t1 && t.length < 5000; x += step) t.push(x)
  }
  const idx = (ts: number) => (t.length ? Math.round((ts - t[0]!) / step) : -1)
  const hops = s.series.map((x) => {
    const values: (number | null)[] = t.map(() => null)
    for (const p of x.points) {
      const i = idx(Date.parse(p.t))
      if (i >= 0 && i < t.length) values[i] = p.lat_p50_ms ?? null
    }
    return { name: `${nameOf(x.from)} → ${nameOf(x.to)}${s.series.filter((y) => y.from === x.from && y.to === x.to).length > 1 ? ` · e${x.epoch}` : ''}`, values }
  })
  const last = s.series[s.series.length - 1]
  const e2e: (number | null)[] = t.map(() => null)
  const frames: (number | null)[] = t.map(() => null)
  const gaps: (number | null)[] = t.map(() => null)
  const suspect = t.map(() => false)
  for (const x of s.series) {
    for (const p of x.points) {
      const i = idx(Date.parse(p.t))
      if (i < 0 || i >= t.length) continue
      if (x === last) {
        e2e[i] = p.e2e_p50_ms ?? null
        frames[i] = p.frames ?? null
        gaps[i] = p.gap_frames ?? null
      }
      if (p.clock_suspect) suspect[i] = true
    }
  }
  const from = t[0] ?? 0
  const to = t[t.length - 1] ?? 0
  const markers = journal
    .filter((e) => e.kind === 'relay_resume' || e.kind === 'relay_gap')
    .map((e) => {
      const d = e.details ?? {}
      const kind: MarkerKind = e.kind === 'relay_gap' ? 'gap' : 'resume'
      const label = kind === 'gap' ? `gap ${String(d.gap_frames ?? '?')} frames` : `resume ${ms(Number(d.outage_ms ?? NaN))}`
      return { t: Date.parse(e.t), kind, label }
    })
    .filter((m) => m.t >= from && m.t <= to)
  return { t, hops, e2e, frames, gaps, suspect, markers }
}

const GRID: CSSProperties = { stroke: 'var(--chart-grid)' }

const W = 900
const PAD_L = 56
const PAD_R = 14

function niceMax(v: number) {
  const steps = [10, 20, 25, 50, 60, 80, 100, 150, 200, 300, 500, 1000, 1500, 2000, 3000, 5000]
  return steps.find((s) => s >= v) ?? Math.ceil(v / 1000) * 1000
}

function pathOf(vals: (number | null)[], x: (i: number) => number, y: (v: number) => number) {
  let d = ''
  let pen = false
  vals.forEach((v, i) => {
    if (v == null) {
      pen = false
      return
    }
    d += `${pen ? 'L' : 'M'}${x(i).toFixed(1)} ${y(v).toFixed(1)}`
    pen = true
  })
  return d
}

export function SeriesChart({ c, label }: { c: Chart; label: string }) {
  const [hover, setHover] = useState<number | null>(null)
  const n = c.t.length
  const x = (i: number) => PAD_L + (n > 1 ? i / (n - 1) : 0.5) * (W - PAD_L - PAD_R)
  const t0 = c.t[0] ?? 0
  const t1 = c.t[n - 1] ?? 0
  const xt = (t: number) => PAD_L + (n > 1 ? (t - t0) / (t1 - t0) : 0.5) * (W - PAD_L - PAD_R)
  const H1 = 130
  const H2 = 60
  const H3 = 40
  const top1 = 14
  const top2 = top1 + H1 + 26
  const top3 = top2 + H2 + 26
  const total = top3 + H3 + 22
  const hopVals = c.hops.flatMap((h) => h.values.filter((v): v is number => v != null))
  const hopMax = niceMax(Math.max(10, ...hopVals) * 1.15)
  const y1 = (v: number) => top1 + H1 - (v / hopMax) * H1
  const eVals = c.e2e.filter((v): v is number => v != null)
  const eMin = eVals.length ? Math.max(0, Math.floor((Math.min(...eVals) - 30) / 50) * 50) : 0
  const eMax = eVals.length ? Math.ceil((Math.max(...eVals) + 30) / 50) * 50 : 100
  const y2 = (v: number) => top2 + H2 - ((v - eMin) / Math.max(1, eMax - eMin)) * H2
  const fVals = c.frames.filter((v): v is number => v != null)
  const fMax = Math.max(1, ...fVals)
  const barW = Math.max(1, (W - PAD_L - PAD_R) / Math.max(1, n) - 1)
  const ticks = n > 1 ? [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(f * (n - 1))) : [0]
  const kinds = [...new Set(c.markers.map((m) => m.kind))]

  const onMove = (e: MouseEvent<SVGRectElement>) => {
    const r = e.currentTarget.getBoundingClientRect()
    const f = (e.clientX - r.left) / r.width
    setHover(Math.max(0, Math.min(n - 1, Math.round(f * (n - 1)))))
  }

  return (
    <div className="flex flex-col gap-2" data-testid="series-chart">
      <ul className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-text-soft" aria-label="Legend">
        {c.hops.map((h, i) => (
          <li key={h.name} className="flex items-center gap-1.5">
            <svg width="22" height="6" aria-hidden>
              <line x1="0" x2="22" y1="3" y2="3" style={hopStyle(i).style} strokeWidth="2" strokeDasharray={hopStyle(i).dash} />
            </svg>
            <span className="font-mono">{h.name}</span>
          </li>
        ))}
        <li className="flex items-center gap-1.5">
          <svg width="22" height="6" aria-hidden>
            <line x1="0" x2="22" y1="3" y2="3" className="stroke-text" strokeWidth="2" />
          </svg>
          End-to-end
        </li>
        {kinds.map((k) => (
          <li key={k} className={`flex items-center gap-1 ${MARK[k].text}`}>
            <Icon name={MARK[k].icon} size={12} />
            <span className="text-text-soft">{MARK[k].word}</span>
          </li>
        ))}
        {c.suspect.some(Boolean) && (
          <li className="flex items-center gap-1 text-warn">
            <Icon name="state-warn" size={12} />
            <span className="text-text-soft">clock suspect (amber ticks)</span>
          </li>
        )}
      </ul>
      <div className="overflow-x-auto">
        <div className="relative min-w-[600px]">
          <svg viewBox={`0 0 ${W} ${total}`} width="100%" role="img" aria-label={label} className="block overflow-visible">
            {[0, 0.5, 1].map((f) => (
              <g key={f}>
                <line x1={PAD_L} x2={W - PAD_R} y1={y1(hopMax * f)} y2={y1(hopMax * f)} style={GRID} />
                <text x={PAD_L - 6} y={y1(hopMax * f) + 3} textAnchor="end" className="fill-muted font-mono" fontSize="10">
                  {Math.round(hopMax * f)} ms
                </text>
              </g>
            ))}
            <text x={PAD_L} y={top1 - 4} className="fill-muted font-mono" fontSize="10">
              latency per hop, p50
            </text>
            {c.hops.map((h, i) => (
              <path key={h.name} d={pathOf(h.values, x, y1)} fill="none" style={hopStyle(i).style} strokeWidth="2" strokeDasharray={hopStyle(i).dash} strokeLinejoin="round" strokeLinecap="round" />
            ))}
            {c.suspect.map((sus, i) => sus && <line key={i} x1={x(i)} x2={x(i)} y1={top1 + H1 - 6} y2={top1 + H1} className="stroke-warn" strokeWidth="2" />)}

            {[eMin, eMax].map((v) => (
              <g key={v}>
                <line x1={PAD_L} x2={W - PAD_R} y1={y2(v)} y2={y2(v)} style={GRID} />
                <text x={PAD_L - 6} y={y2(v) + 3} textAnchor="end" className="fill-muted font-mono" fontSize="10">
                  {ms(v)}
                </text>
              </g>
            ))}
            <text x={PAD_L} y={top2 - 4} className="fill-muted font-mono" fontSize="10">
              end-to-end, p50 (OBS bus → platform socket)
            </text>
            <path d={pathOf(c.e2e, x, y2)} fill="none" className="stroke-text" strokeWidth="2" strokeLinejoin="round" />

            <text x={PAD_L} y={top3 - 4} className="fill-muted font-mono" fontSize="10">
              frames per step (lost frames in red)
            </text>
            <line x1={PAD_L} x2={W - PAD_R} y1={top3 + H3} y2={top3 + H3} style={{ stroke: 'var(--chart-axis)' }} />
            <text x={PAD_L - 6} y={top3 + 8} textAnchor="end" className="fill-muted font-mono" fontSize="10">
              {fMax}
            </text>
            {c.frames.map((f, i) =>
              f == null ? null : <rect key={i} x={x(i) - barW / 2} y={top3 + H3 - (f / fMax) * H3} width={barW} height={(f / fMax) * H3} style={{ fill: 'var(--chart-context)' }} />,
            )}
            {c.gaps.map((g, i) => (g ? <rect key={`g${i}`} x={x(i) - barW / 2} y={top3} width={barW} height={H3} className="fill-fail/40" /> : null))}

            {ticks.map((i) => (
              <text key={i} x={x(i)} y={total - 4} textAnchor={i === 0 ? 'start' : i === n - 1 ? 'end' : 'middle'} className="fill-muted font-mono" fontSize="10">
                {clock(c.t[i]).slice(0, 5)}
              </text>
            ))}

            {c.markers.map((m, k) => {
              const mx = xt(m.t)
              return (
                <g key={k}>
                  <line x1={mx} x2={mx} y1={top1} y2={top3 + H3} className={MARK[m.kind].stroke} strokeWidth="1.4" strokeDasharray={MARK[m.kind].dash} />
                  <text x={mx + 4} y={top1 + 10 + (k % 3) * 12} className={`${MARK[m.kind].fill} font-mono`} fontSize="10">
                    {m.label}
                  </text>
                </g>
              )
            })}

            {hover != null && <line x1={x(hover)} x2={x(hover)} y1={top1} y2={top3 + H3} className="stroke-accent" strokeWidth="1" />}
            <rect x={PAD_L} y={top1} width={W - PAD_L - PAD_R} height={top3 + H3 - top1} fill="transparent" onMouseMove={onMove} onMouseLeave={() => setHover(null)} />
          </svg>
          {hover != null && (
            <div
              className="pointer-events-none absolute top-2 z-10 min-w-[190px] rounded-md border border-line-2 bg-panel px-2.5 py-2 text-xs shadow-pop"
              style={{ left: `${(x(hover) / W) * 100}%`, transform: hover > n / 2 ? 'translateX(calc(-100% - 10px))' : 'translateX(10px)' }}
            >
              <div className="tabular mb-1 text-muted">{clock(c.t[hover])}</div>
              {c.hops.map((h, i) => (
                <div key={h.name} className="flex items-center justify-between gap-3">
                  <span className="flex items-center gap-1.5 text-text-soft">
                    <span className="inline-block h-0.5 w-3" style={{ background: hopColour(i) }} aria-hidden />
                    hop
                  </span>
                  <span className="tabular text-text">{ms(h.values[hover])}</span>
                </div>
              ))}
              <div className="mt-1 flex justify-between gap-3 border-t border-line pt-1">
                <span className="text-text-soft">End-to-end</span>
                <span className="tabular text-text">{ms(c.e2e[hover])}</span>
              </div>
              <div className="flex justify-between gap-3">
                <span className="text-text-soft">Frames</span>
                <span className="num text-text">
                  {c.frames[hover] ?? '—'}
                  {c.gaps[hover] ? <span className="text-fail"> · {c.gaps[hover]} lost</span> : null}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
